import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useAuth } from '@/auth/useAuth';
import { apiGet } from '@/api/client';
import { type Role, parseRole, ROLES } from '@/auth/roles';

type UserProfile = {
  id?: string;
  name?: string;
  email?: string;
  role: Role;
};

/** Set when GET /v1/users/me returns 403 USER_NOT_PROVISIONED (SSO user not in DB). */
export const USER_NOT_PROVISIONED = 'USER_NOT_PROVISIONED';

type UserContextValue = {
  user: UserProfile | null;
  role: Role;
  loading: boolean;
  notProvisioned: boolean;
  refetch: () => void;
};

const UserContext = createContext<UserContextValue>({
  user: null,
  role: ROLES.READER,
  loading: true,
  notProvisioned: false,
  refetch: () => {},
});

export function UserProvider({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuth();
  const [user, setUser] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [notProvisioned, setNotProvisioned] = useState(false);

  const fetchUser = useCallback(() => {
    if (!isAuthenticated) {
      setUser(null);
      setNotProvisioned(false);
      setLoading(false);
      return;
    }
    setLoading(true);
    setNotProvisioned(false);
    apiGet<{ id?: string; name?: string; email?: string; role?: string }>('/v1/users/me')
      .then((data) => {
        setUser({
          id: data.id,
          name: data.name,
          email: data.email,
          role: parseRole(data.role),
        });
      })
      .catch((err: Error & { code?: string }) => {
        if (err?.code === USER_NOT_PROVISIONED) {
          setNotProvisioned(true);
          setUser(null);
        } else {
          setUser({ role: ROLES.READER });
        }
      })
      .finally(() => setLoading(false));
  }, [isAuthenticated]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const value: UserContextValue = {
    user,
    role: user?.role ?? ROLES.READER,
    loading,
    notProvisioned,
    refetch: fetchUser,
  };

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
}

export function useUser() {
  const ctx = useContext(UserContext);
  if (!ctx) throw new Error('useUser must be used within UserProvider');
  return ctx;
}
